import { produce } from 'immer';
import type { EodSummaryReportRow } from '../../../interface/eodClickhouseInterface';
import {
  FETCH_EOD_SUMMARY_REPORT_REQUEST,
  FETCH_EOD_SUMMARY_REPORT_SUCCESS,
  FETCH_EOD_SUMMARY_REPORT_FAILURE,
  FETCH_EOD_FUNDS_REPORT_REQUEST,
  FETCH_EOD_FUNDS_REPORT_SUCCESS,
  FETCH_EOD_FUNDS_REPORT_FAILURE,
} from './eodClickhouseConstants';

const initialState: {
  eodSummaryReportLoading: boolean;
  /** The current page of business dates — the endpoint is server-paginated. */
  eodSummaryReport: EodSummaryReportRow[];
  /**
   * Backend-computed totals across the WHOLE range, not just this page.
   * Kept verbatim, never re-derived from `rows`.
   */
  eodSummaryReportTotals: EodSummaryReportRow | null;
  eodSummaryReportTotalElements: number;
  eodSummaryReportTotalPages: number;
  eodSummaryReportFailure: boolean;

  /**
   * Funds table — the same endpoint read for `creditSales` / `maghilFunding`,
   * held separately because that table paginates and sorts independently of the
   * Summary Report table on the same screen.
   */
  eodFundsReportLoading: boolean;
  eodFundsReport: EodSummaryReportRow[];
  eodFundsReportTotals: EodSummaryReportRow | null;
  eodFundsReportTotalElements: number;
  eodFundsReportTotalPages: number;
  eodFundsReportFailure: boolean;
} = {
  eodSummaryReportLoading: false,
  eodSummaryReport: [],
  eodSummaryReportTotals: null,
  eodSummaryReportTotalElements: 0,
  eodSummaryReportTotalPages: 1,
  eodSummaryReportFailure: false,

  eodFundsReportLoading: false,
  eodFundsReport: [],
  eodFundsReportTotals: null,
  eodFundsReportTotalElements: 0,
  eodFundsReportTotalPages: 1,
  eodFundsReportFailure: false,
};

export type EodClickhouseState = typeof initialState;

export default function eodClickhouseReducer(state = initialState, action: any): EodClickhouseState {
  return produce(state, (draft) => {
    switch (action.type) {
      case FETCH_EOD_SUMMARY_REPORT_REQUEST:
        draft.eodSummaryReportLoading = true;
        draft.eodSummaryReportFailure = false;
        break;
      case FETCH_EOD_SUMMARY_REPORT_SUCCESS:
        draft.eodSummaryReportLoading = false;
        draft.eodSummaryReport = action.payload?.rows ?? [];
        draft.eodSummaryReportTotals = action.payload?.totals ?? null;
        draft.eodSummaryReportTotalElements = Number(action.payload?.totalElements) || 0;
        draft.eodSummaryReportTotalPages = Number(action.payload?.totalPages) || 1;
        draft.eodSummaryReportFailure = false;
        break;
      case FETCH_EOD_SUMMARY_REPORT_FAILURE:
        draft.eodSummaryReportLoading = false;
        draft.eodSummaryReport = [];
        draft.eodSummaryReportTotals = null;
        draft.eodSummaryReportTotalElements = 0;
        draft.eodSummaryReportTotalPages = 1;
        draft.eodSummaryReportFailure = true;
        break;

      case FETCH_EOD_FUNDS_REPORT_REQUEST:
        draft.eodFundsReportLoading = true;
        draft.eodFundsReportFailure = false;
        break;
      case FETCH_EOD_FUNDS_REPORT_SUCCESS:
        draft.eodFundsReportLoading = false;
        draft.eodFundsReport = action.payload?.rows ?? [];
        draft.eodFundsReportTotals = action.payload?.totals ?? null;
        draft.eodFundsReportTotalElements = Number(action.payload?.totalElements) || 0;
        draft.eodFundsReportTotalPages = Number(action.payload?.totalPages) || 1;
        draft.eodFundsReportFailure = false;
        break;
      case FETCH_EOD_FUNDS_REPORT_FAILURE:
        draft.eodFundsReportLoading = false;
        draft.eodFundsReport = [];
        draft.eodFundsReportTotals = null;
        draft.eodFundsReportTotalElements = 0;
        draft.eodFundsReportTotalPages = 1;
        draft.eodFundsReportFailure = true;
        break;

      default:
        return state;
    }
  });
}
